import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
    useAdminPeriods,
    useCreatePeriod,
    useUpdatePeriod,
    useDeletePeriod,
    useRemovePeriodCardBackground,
} from "@/services/adminPeriodService";
import type { Period, PeriodPayload } from "@/services/adminPeriodService";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Edit2, Plus, Trash2, CalendarRange, Loader2, Search } from "lucide-react";

// Helpers
const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric"
    });
};
const formatToInputDate = (dateString?: string) => {
    if (!dateString) return "";
    return dateString.substring(0, 10);
};
// Untuk field <input type="datetime-local"> (format "YYYY-MM-DDTHH:mm")
const formatToInputDateTime = (dateString?: string) => {
    if (!dateString) return "";
    return dateString.replace(" ", "T").substring(0, 16);
};
// Kembalikan "YYYY-MM-DDTHH:mm" dari datetime-local jadi "YYYY-MM-DD HH:mm:ss" untuk backend
const toBackendDateTime = (value: string) => {
    if (!value) return value;
    const withSeconds = value.length === 16 ? `${value}:00` : value;
    return withSeconds.replace("T", " ");
};
const formatDateTime = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString.replace(" ", "T")).toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
// Status buka/tutup jendela pembayaran berdasarkan waktu saat ini (dibandingkan sampai ke jam)
const getPaymentWindowStatus = (start?: string, end?: string) => {
    if (!start || !end) return { label: "Belum diatur", className: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" };
    const now = new Date();
    const startDt = new Date(start.replace(" ", "T"));
    const endDt = new Date(end.replace(" ", "T"));
    if (now < startDt) return { label: "Belum Dimulai", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" };
    if (now > endDt) return { label: "Ditutup", className: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300" };
    return { label: "Dibuka", className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" };
};

// Schema Validation
const formSchema = z.object({
    name: z.string().min(1, "Nama periode wajib diisi"),
    start_date: z.string().min(1, "Tanggal mulai wajib diisi"),
    end_date: z.string().min(1, "Tanggal selesai wajib diisi"),
    start_date_payments: z.string().min(1, "Tanggal mulai pembayaran wajib diisi"),
    end_date_payments: z.string().min(1, "Tanggal selesai pembayaran wajib diisi"),
    is_active: z.boolean().default(false),
});
type FormValues = z.infer<typeof formSchema>;

export default function PeriodsPage() {
    const { data: periods, isLoading } = useAdminPeriods();
    const createMutation = useCreatePeriod();
    const updateMutation = useUpdatePeriod();
    const deleteMutation = useDeletePeriod();
    const removeBackgroundMutation = useRemovePeriodCardBackground();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState<Period | null>(null);
    const [frontFile, setFrontFile] = useState<File | null>(null);
    const [backFile, setBackFile] = useState<File | null>(null);
    const [layoutOverrides, setLayoutOverrides] = useState({
        qr_x: "", qr_y: "", qr_size: "", text_x: "", text_zone_top: "", text_zone_bottom: "",
    });

    // Alert dialog state
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    // Client-side pagination and search state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");

    // Client-side filtering and pagination logic
    const filteredPeriods = (periods || []).filter(period => 
        period.name.toLowerCase().includes(search.toLowerCase())
    );
    const totalItems = filteredPeriods.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const paginatedPeriods = filteredPeriods.slice((page - 1) * perPage, page * perPage);
    const fromItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
    const toItem = Math.min(page * perPage, totalItems);

    const activePeriodsCount = (periods || []).filter(p => p.is_active).length;
    const inactivePeriodsCount = (periods || []).filter(p => !p.is_active).length;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            start_date: "",
            end_date: "",
            start_date_payments: "",
            end_date_payments: "",
            is_active: false,
        },
    });

    const handleOpenDialog = (period?: Period) => {
        setFrontFile(null);
        setBackFile(null);
        if (period) {
            setEditingPeriod(period);
            form.reset({
                name: period.name,
                start_date: formatToInputDate(period.start_date),
                end_date: formatToInputDate(period.end_date),
                start_date_payments: formatToInputDateTime(period.start_date_payments),
                end_date_payments: formatToInputDateTime(period.end_date_payments),
                is_active: Boolean(period.is_active),
            });
            setLayoutOverrides({
                qr_x: period.qr_x?.toString() ?? "",
                qr_y: period.qr_y?.toString() ?? "",
                qr_size: period.qr_size?.toString() ?? "",
                text_x: period.text_x?.toString() ?? "",
                text_zone_top: period.text_zone_top?.toString() ?? "",
                text_zone_bottom: period.text_zone_bottom?.toString() ?? "",
            });
        } else {
            setEditingPeriod(null);
            form.reset({
                name: "",
                start_date: "",
                end_date: "",
                start_date_payments: "",
                end_date_payments: "",
                is_active: false,
            });
            setLayoutOverrides({ qr_x: "", qr_y: "", qr_size: "", text_x: "", text_zone_top: "", text_zone_bottom: "" });
        }
        setIsDialogOpen(true);
    };

    const onSubmit = (values: FormValues) => {
        const payload: PeriodPayload = {
            ...values,
            start_date: values.start_date + " 00:00:00",
            end_date: values.end_date + " 23:59:59",
            start_date_payments: toBackendDateTime(values.start_date_payments),
            end_date_payments: toBackendDateTime(values.end_date_payments),
        };
        // Field override posisi QR/teks (kosong = pakai default sistem, tidak dikirim ke server).
        (Object.keys(layoutOverrides) as (keyof typeof layoutOverrides)[]).forEach((key) => {
            const raw = layoutOverrides[key];
            if (raw !== "" && !isNaN(Number(raw))) {
                (payload as Record<string, unknown>)[key] = Number(raw);
            }
        });

        // Kalau ada file background yang dipilih, kirim sebagai FormData (multipart).
        // Kalau tidak ada perubahan background, cukup JSON biasa seperti sebelumnya.
        const buildFormData = () => {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
                }
            });
            if (frontFile) formData.append("card_background_front", frontFile);
            if (backFile) formData.append("card_background_back", backFile);
            return formData;
        };

        const hasFiles = !!(frontFile || backFile);

        if (editingPeriod) {
            updateMutation.mutate(
                { id: editingPeriod.period_id, payload: hasFiles ? buildFormData() : payload },
                {
                    onSuccess: () => {
                        toast.success("Periode berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: () => toast.error("Gagal memperbarui periode"),
                }
            );
        } else {
            createMutation.mutate(hasFiles ? buildFormData() : payload, {
                onSuccess: () => {
                    toast.success("Periode berhasil dibuat");
                    setIsDialogOpen(false);
                },
                onError: () => toast.error("Gagal membuat periode"),
            });
        }
    };

    const handleRemoveBackground = (side: "front" | "back") => {
        if (!editingPeriod) return;
        removeBackgroundMutation.mutate(
            { id: editingPeriod.period_id, side },
            {
                onSuccess: (updated) => {
                    setEditingPeriod(updated);
                    toast.success(`Background ${side === "front" ? "depan" : "belakang"} dihapus, kembali ke bawaan sistem`);
                },
                onError: () => toast.error("Gagal menghapus background"),
            }
        );
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Periode",
            description: "Apakah Anda yakin ingin menghapus periode ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Periode berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus periode"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    const isMutating = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <CalendarRange className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kelola Periode</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola daftar periode pendaftaran untuk Konaspi.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Periode
                    </Button>
                </div>
            </div>

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <CalendarRange className="w-12 h-12 text-indigo-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><CalendarRange className="w-4 h-4 text-indigo-500" /> Total Periode</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{(periods || []).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <CalendarRange className="w-12 h-12 text-emerald-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Aktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{activePeriodsCount}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <CalendarRange className="w-12 h-12 text-rose-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-rose-500"></div> Nonaktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{inactivePeriodsCount}</p>
                </div>
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari nama periode..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="flex justify-center items-center py-20">
                                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                            </div>
                        ) : (
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                    <tr>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama Periode</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Tgl Pendaftaran</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Jendela Pembayaran (+Jam)</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Status Pembayaran</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Periode Aktif</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {paginatedPeriods && paginatedPeriods.length > 0 ? paginatedPeriods.map((period, index) => (
                                        <tr key={period.period_id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-6 py-4 text-center font-medium">{(page - 1) * perPage + index + 1}</td>
                                            <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">{period.name}</td>
                                            <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                                                {formatDate(period.start_date)} - {formatDate(period.end_date)}
                                            </td>
                                            <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                                                {formatDateTime(period.start_date_payments)} &ndash; {formatDateTime(period.end_date_payments)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {(() => {
                                                    const status = getPaymentWindowStatus(period.start_date_payments, period.end_date_payments);
                                                    return (
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                                                            {status.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${period.is_active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'}`}>
                                                    {period.is_active ? "Aktif" : "Tidak Aktif"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        variant="outline" 
                                                        size="icon"
                                                        className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                        onClick={() => handleOpenDialog(period)}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="icon"
                                                        className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                                        onClick={() => handleDelete(period.period_id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={7} className="p-12 text-center text-slate-500">
                                                Belum ada data periode
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-3 text-slate-500 font-medium">
                                <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                                <div className="flex items-center gap-2 sm:flex">
                                    <label htmlFor="perPage">Tampilkan:</label>
                                    <select
                                        id="perPage"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="rounded-lg"
                                    >
                                        Sebelumnya
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="rounded-lg"
                                    >
                                        Selanjutnya
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPeriod ? "Edit Periode" : "Tambah Periode Baru"}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nama Periode <span className="text-red-500">*</span></FormLabel>
                                        <FormControl>
                                            <Input placeholder="Contoh: Gelombang 1" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="start_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tanggal Mulai <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input type="date" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="end_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tanggal Selesai <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input type="date" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">
                                    Sistem mengecek batas pembayaran sampai ke jamnya (bukan hanya tanggal) — pastikan jam selesai diisi mendekati akhir hari (mis. 23:59) kalau ingin pembayaran tetap bisa dibuat sepanjang tanggal tersebut.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="start_date_payments"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Tgl &amp; Jam Mulai Pembayaran</FormLabel>
                                            <FormControl>
                                                <Input type="datetime-local" {...field} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="end_date_payments"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-slate-700 dark:text-slate-300 font-semibold">Tgl &amp; Jam Selesai Pembayaran</FormLabel>
                                            <FormControl>
                                                <Input type="datetime-local" {...field} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <FormLabel>Background Kartu Peserta (Opsional)</FormLabel>
                                <p className="text-xs text-muted-foreground">
                                    Kosongkan untuk memakai desain kartu bawaan sistem. Berlaku untuk kartu peserta konvensi/individu (RFID & PIC) di periode ini.
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 block mb-1">Kartu Depan</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
                                            className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-600 file:text-xs file:font-bold"
                                        />
                                        {(frontFile || editingPeriod?.card_background_front_url) && (
                                            <div className="mt-2 space-y-1">
                                                <img
                                                    src={frontFile ? URL.createObjectURL(frontFile) : editingPeriod?.card_background_front_url || ""}
                                                    alt="Preview kartu depan"
                                                    className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                                                />
                                                {!frontFile && editingPeriod?.card_background_front_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveBackground("front")}
                                                        className="text-[11px] text-rose-500 hover:text-rose-600 font-semibold"
                                                    >
                                                        Hapus, pakai bawaan sistem
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 block mb-1">Kartu Belakang</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setBackFile(e.target.files?.[0] || null)}
                                            className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-600 file:text-xs file:font-bold"
                                        />
                                        {(backFile || editingPeriod?.card_background_back_url) && (
                                            <div className="mt-2 space-y-1">
                                                <img
                                                    src={backFile ? URL.createObjectURL(backFile) : editingPeriod?.card_background_back_url || ""}
                                                    alt="Preview kartu belakang"
                                                    className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                                                />
                                                {!backFile && editingPeriod?.card_background_back_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveBackground("back")}
                                                        className="text-[11px] text-rose-500 hover:text-rose-600 font-semibold"
                                                    >
                                                        Hapus, pakai bawaan sistem
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <FormLabel>Posisi QR & Teks (Opsional)</FormLabel>
                                <p className="text-xs text-muted-foreground">
                                    Kosongkan untuk pakai posisi bawaan sistem. Isi kalau background di atas desainnya beda
                                    (mis. candi di posisi lain) sehingga QR/teks perlu digeser. Koordinat berbasis kanvas 957x1524px.
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">QR - X (default 90)</label>
                                        <Input type="number" placeholder="90" value={layoutOverrides.qr_x}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_x: e.target.value }))} className="h-9" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">QR - Y (default 500)</label>
                                        <Input type="number" placeholder="500" value={layoutOverrides.qr_y}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_y: e.target.value }))} className="h-9" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">QR - Ukuran (default 330)</label>
                                        <Input type="number" placeholder="330" value={layoutOverrides.qr_size}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, qr_size: e.target.value }))} className="h-9" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">Teks - X (default 90)</label>
                                        <Input type="number" placeholder="90" value={layoutOverrides.text_x}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_x: e.target.value }))} className="h-9" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">Teks - Zona Atas (default 860)</label>
                                        <Input type="number" placeholder="860" value={layoutOverrides.text_zone_top}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_zone_top: e.target.value }))} className="h-9" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-500 block mb-1">Teks - Zona Bawah (default 1180)</label>
                                        <Input type="number" placeholder="1180" value={layoutOverrides.text_zone_bottom}
                                            onChange={(e) => setLayoutOverrides((v) => ({ ...v, text_zone_bottom: e.target.value }))} className="h-9" />
                                    </div>
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">
                                                Status Aktif
                                            </FormLabel>
                                            <div className="text-xs text-muted-foreground">
                                                Menjadikan periode ini sebagai periode pendaftaran saat ini. (Periode aktif sebelumnya akan dinonaktifkan)
                                            </div>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Custom Alert Dialog for Confirmations */}
            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive 
                                    ? "bg-rose-600 hover:bg-rose-700" 
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
